import { useCallback, useEffect, useRef, useState } from "react";
import { BaseNotificationItem } from "./notifications.types";

const DEFAULT_AUTO_DISMISS_DELAY_MS = 5 * 1000;

export type ToastNotificationItem = BaseNotificationItem;

type ShowToastArgs = Omit<ToastNotificationItem, "id">;

export type UseToastNotificationsStateOptions = {
  autoDismissDelayMs?: number;
};

export type UseToastNotificationsResult = {
  toasts: ToastNotificationItem[];
  showToast: (notification: ShowToastArgs) => number;
  removeToast: (notificationId: number) => void;
  clearToasts: () => void;
};

export const useToastNotificationsState = ({
  autoDismissDelayMs = DEFAULT_AUTO_DISMISS_DELAY_MS,
}: UseToastNotificationsStateOptions = {}): UseToastNotificationsResult => {
  const [toasts, setToasts] = useState<ToastNotificationItem[]>([]);
  const nextNotificationIdRef = useRef(0);
  const notificationTimersRef = useRef<Map<number, number>>(new Map());

  const clearNotificationTimer = useCallback((notificationId: number) => {
    const timerId = notificationTimersRef.current.get(notificationId);
    if (timerId !== undefined) {
      window.clearTimeout(timerId);
      notificationTimersRef.current.delete(notificationId);
    }
  }, []);

  // Clear all timers when the component unmounts to avoid memory leaks.
  useEffect(() => {
    return () => {
      notificationTimersRef.current.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      notificationTimersRef.current.clear();
    };
    // it should be executed only once when the component is mounted, so we can ignore the exhaustive-deps rule here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeToast = useCallback(
    (notificationId: number) => {
      clearNotificationTimer(notificationId);
      setToasts((previousToasts) =>
        previousToasts.filter(
          (notification) => notification.id !== notificationId
        )
      );
    },
    [clearNotificationTimer]
  );

  const showToast = useCallback(
    ({ text, title }: ShowToastArgs) => {
      const notificationId = nextNotificationIdRef.current;
      nextNotificationIdRef.current += 1;

      setToasts((previousToasts) => [
        ...previousToasts,
        { id: notificationId, text, title },
      ]);

      const timerId = window.setTimeout(() => {
        removeToast(notificationId);
      }, autoDismissDelayMs);

      notificationTimersRef.current.set(notificationId, timerId);

      return notificationId;
    },
    [autoDismissDelayMs, removeToast]
  );

  const clearToasts = useCallback(() => {
    notificationTimersRef.current.forEach((timerId) => {
      window.clearTimeout(timerId);
    });
    notificationTimersRef.current.clear();
    setToasts([]);
  }, []);

  return {
    toasts,
    showToast,
    removeToast,
    clearToasts,
  };
};
